import { TranslateService } from '@ngx-translate/core';
import { Component, ViewChild } from '@angular/core';
import { UserPasswordExpiration } from 'src/app/core/model/userPasswordExpiration';
import { MessageHandlerService } from 'src/app/core/utility/message-handler.service';
import { UntypedFormBuilder, UntypedFormControl, UntypedFormGroup, ValidationErrors, ValidatorFn, Validators } from '@angular/forms';
import { SettingService } from 'src/app/core/service/setting.service';
import { UserService } from 'src/app/core/service/user.service';
import { LanguageService } from 'src/app/core/service/language.service';
import { AuthenticationService } from 'src/app/core/authentication/authentication.service';
import { ButtonComponent } from '@progress/kendo-angular-buttons';

@Component({
  selector: 'app-change-password',
  templateUrl: './change-password.component.html',
  styleUrls: ['./change-password.component.scss']
})
export class ChangePasswordComponent 
{
  @ViewChild('passwordControl') public passwordControl: ButtonComponent;
  @ViewChild('closeButton') public closeButton: ButtonComponent;

  public dialogOpened = false;
  public roleSelectionPromise: Promise<string>;
  public expirationInfo: UserPasswordExpiration;
  public expireDays: number;
  public canCancel: boolean;
  public daysText = '';
  public changePasswordForm: any = null;
  public minPasswordLength: number;
  public maxPasswordLength: number;
  public passwordPattern: string;
  public passwordMeterColors: any[];
  public invalidPasswordText = '';
  public selectedUser: any;
  public updating = false;
  public closing = false;

  private resolve: any;

  constructor(
    private authenticationService: AuthenticationService,
    private settingService: SettingService,
    private formBuilder: UntypedFormBuilder,
    private userService: UserService,
    private messageHandlerService: MessageHandlerService,
    private languageService: LanguageService,
    private translate: TranslateService) 
  {
    this.passwordMeterColors = this.userService.getPasswordMeterColors();
  }

  public get password(): UntypedFormControl
  {
    return this.changePasswordForm.get('password');
  }

  public get passwordConfirm(): UntypedFormControl
  {
    return this.changePasswordForm.get('passwordConfirm');
  }

  /**
   * Execute method called during login when password is either closes to expiration or expired.
   * 
   * @param userPasswordExpiration User password expiration information
   * @returns Promise when user interaction is completed
   */
  public execute(userPasswordExpiration: UserPasswordExpiration): Promise<string> 
  {
    this.getUserSettings();
    this.getSessionSettings();

    this.expirationInfo = userPasswordExpiration;
    this.expireDays = userPasswordExpiration.passwordExpirationDays - userPasswordExpiration.daysSince;
    this.canCancel = this.expireDays >= 1;
    this.roleSelectionPromise = new Promise(resolve => { this.resolve = resolve; });

    this.translate.get(['CHANGE-PASSWORD-EXPIRING-DAYS']).subscribe({
      next: (res) =>
      {
        this.daysText = res['CHANGE-PASSWORD-EXPIRING-DAYS'];
        this.dialogOpened = true;
        setTimeout(() => { if (this.passwordControl) { this.passwordControl.nativeElement.focus(); } }, 150);
      }
    });
    return this.roleSelectionPromise;
  }

  /**
   * Manually closing the dialog. This is allowed only if the password is not expired yet.
   */
  public close(event: Event): void
  {
    event.preventDefault();
    this.dialogOpened = false;
    this.resolve(this.canCancel ? 'cancel' : null);
  }

  /**
   * Save the user new password.
   */
  public savePassword(): void
  {
    this.updating = true;
    this.userService.getUserById(this.authenticationService.currentUser.id, false).subscribe({
      next: (user) =>
      {
        this.selectedUser = user;
        this.selectedUser.password = this.changePasswordForm.controls.password.value;
        this.userService.saveUser(this.selectedUser, false, this.selectedUser.id).subscribe({
          next: () =>
          {
            this.messageHandlerService.displaySuccess('PASSWORD-SAVED-SUCCESSFULLY');
            this.dialogOpened = false;
            this.resolve('password-updated');
          },
          error: (errorResponse) =>
          {
            this.messageHandlerService.displayError(errorResponse);
            this.updating = false;
          }
        });
      },
      error: (errorResponse) =>
      {
        this.messageHandlerService.displayError(errorResponse);
        this.updating = false;
        this.resolve('cancel');   // If user can't be retrieve then we must cancel password change
      }
    });
  }

  /**
   * Check if form is valid
   * @returns True if the form is valid
   */
  public formValid(): boolean
  {
    return this.changePasswordForm && this.changePasswordForm.valid;
  }

  /**
   * Check if form is dirty
   * @returns True if the form is dirty
   */
  public formDirty(): boolean
  {
    return this.changePasswordForm && this.changePasswordForm.dirty;
  }

  /**
   * Get session settings to set the invalidPasswordText according to the current selected language.
   */
  private getSessionSettings(): void
  {
    this.settingService.getCachedSessionSettings().subscribe({
      next: (sessionSetting) =>
      {
        // Get Password Pattern Text
        const languageIndex = this.languageService.currentLanguage === 'es' ? 1 :
          this.languageService.currentLanguage === 'de' ? 2 : 0;
        this.invalidPasswordText = sessionSetting.passwordPatternTexts[languageIndex];
      }
    });
  }

  /**
   * Build the user change password form
   */
  private buildForm(): void
  {
    this.changePasswordForm = this.formBuilder.group({
      password: [
        { value: null, disabled: false },
        {
          validators: [
            Validators.required,
            Validators.minLength(this.minPasswordLength),
            Validators.pattern(this.passwordPattern)
          ]
        }
      ],
      passwordConfirm: [null, Validators.required]
    });

    const passwordConfirmValidators = [];
    passwordConfirmValidators.push(this.passwordMatchValidator());

    this.changePasswordForm.controls.passwordConfirm.setValidators(passwordConfirmValidators);
    this.changePasswordForm.controls.passwordConfirm.updateValueAndValidity();

    this.changePasswordForm.get('password').valueChanges.subscribe({
      next: () =>
      {
        this.changePasswordForm.get('passwordConfirm').updateValueAndValidity();
      }
    });
  }

  /**
   * Get user setting for password requirements.
   */
  private getUserSettings()
  {
    this.settingService.getCachedUserSettings().subscribe({
      next: (userSetting) =>
      {
        // Set username, password and image size from settings
        this.minPasswordLength = userSetting.passwordMin;
        this.maxPasswordLength = userSetting.passwordMax;
        this.passwordPattern = userSetting.passwordPattern;
        this.buildForm();
      },
      error: (errorResponse) =>
      {
        this.messageHandlerService.displayError(errorResponse);
      }
    });
  }

  /**
   * Password match validator
   * @returns ValidatorFn Validator function
   */
  private passwordMatchValidator(): ValidatorFn
  {
    return (control: UntypedFormGroup): ValidationErrors | null =>
    {
      const password = this.password.value ? this.password.value : '';
      const passwordConfirm = control.value ? control.value : '';
      if (password !== passwordConfirm)
      {
        return {
          unmatchConfirmPassword: true
        };
      }
      return null;
    };
  }
}
